"use client";

import type { ReactNode } from "react";
import {
  BarChart3,
  Bookmark,
  Building,
  ClipboardList,
  DollarSign,
  LogOut,
  MapPin,
  Megaphone,
  MessageCircle,
  Settings,
  SlidersHorizontal,
  Truck,
  User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { CommerceIcons } from "@/lib/icons/commerce-icons";
import { ProfileStatsIcons } from "@/lib/icons/profile-stats-icons";
import {
  PaymentStatusPill,
  type PaymentStatus,
} from "@/components/settings/payment-status-pill";

export type AccountMenuRow = {
  key: string;
  href?: string;
  icon: LucideIcon;
  label: string;
  badgeCount?: number;
  destructive?: boolean;
  onAction?: () => void;
  trailing?: ReactNode;
};

export type AccountMenuSection = {
  key: string;
  title: string;
  rows: AccountMenuRow[];
};

type BuildAccountMenuSectionsOptions = {
  tNav: (key: string) => string;
  tSettings: (key: string) => string;
  tCommon: (key: string) => string;
  myProfileHref: string;
  offerActionCount: number;
  paymentStatus: PaymentStatus;
  onSignOut: () => void;
};

export function buildAccountMenuSections({
  tNav,
  tSettings,
  tCommon,
  myProfileHref,
  offerActionCount,
  paymentStatus,
  onSignOut,
}: BuildAccountMenuSectionsOptions): AccountMenuSection[] {
  return [
    {
      key: "account",
      title: tNav("sectionAccount"),
      rows: [
        {
          key: "profile",
          href: myProfileHref,
          icon: User,
          label: tNav("myProfile"),
        },
        {
          key: "settings",
          href: "/settings",
          icon: Settings,
          label: tNav("settings"),
        },
      ],
    },
    {
      key: "sell",
      title: tNav("sectionSell"),
      rows: [
        {
          key: "listings",
          href: "/my-listings",
          icon: ProfileStatsIcons.listings,
          label: tNav("myListings"),
        },
        {
          key: "orders-seller",
          href: "/order-management",
          icon: ClipboardList,
          label: tNav("orderManagement"),
        },
        {
          key: "payments",
          href: "/settings/payments",
          icon: Building,
          label: tSettings("payments.title"),
          trailing: <PaymentStatusPill status={paymentStatus} />,
        },
        {
          key: "shipping",
          href: "/shipping-settings",
          icon: Truck,
          label: tNav("shippingSettings"),
        },
        {
          key: "analytics",
          href: "/analytics",
          icon: BarChart3,
          label: tNav("analytics"),
        },
        {
          key: "currency",
          href: "/settings/currency",
          icon: DollarSign,
          label: tSettings("currency.title"),
        },
      ],
    },
    {
      key: "buy",
      title: tNav("sectionBuy"),
      rows: [
        {
          key: "orders-placed",
          href: "/orders-placed",
          icon: CommerceIcons.buyNow,
          label: tNav("ordersPlaced"),
        },
        {
          key: "saved",
          href: "/saved",
          icon: Bookmark,
          label: tNav("saved"),
        },
        {
          key: "offers",
          href: "/offers",
          icon: CommerceIcons.offer,
          label: tNav("offers"),
          badgeCount: offerActionCount,
        },
        {
          key: "addresses",
          href: "/settings/addresses",
          icon: MapPin,
          label: tSettings("addressBook.title"),
        },
      ],
    },
    {
      key: "more",
      title: tNav("sectionMore"),
      rows: [
        {
          key: "changelog",
          href: "/changelog",
          icon: Megaphone,
          label: tNav("changelog"),
        },
        {
          key: "contact",
          href: "/contact",
          icon: MessageCircle,
          label: tNav("contactUs"),
        },
        {
          key: "signout",
          icon: LogOut,
          label: tCommon("logOut"),
          destructive: true,
          onAction: onSignOut,
        },
      ],
    },
  ];
}
